import { CONFIG } from '../config/CONFIG.js';
import { AudioEngine } from '../audio/AudioEngine.js';
import type { TimerInterface } from './types.js';

let timerDisplay: HTMLElement | null = null;
let timerStartBtn: HTMLElement | null = null;
let playBtn: HTMLElement | null = null;

export function initTimerElements(
  display: HTMLElement,
  startBtn: HTMLElement,
  playButton: HTMLElement
) {
  timerDisplay = display;
  timerStartBtn = startBtn;
  playBtn = playButton;
}

export const Timer: TimerInterface = {
  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  },

  updateDisplay() {
    if (timerDisplay) {
      timerDisplay.textContent = this.formatTime(CONFIG.timer.remaining);

      if (CONFIG.timer.remaining <= 60 && CONFIG.timer.isRunning) {
        timerDisplay.classList.add('warning');
      } else {
        timerDisplay.classList.remove('warning');
      }
    }
  },

  onTimerEnd() {
    CONFIG.timer.isRunning = false;
    if (CONFIG.timer.intervalId) {
      clearInterval(CONFIG.timer.intervalId);
    }

    if (timerStartBtn) {
      timerStartBtn.textContent = 'Start';
      timerStartBtn.classList.remove('running');
    }

    this.playAlarm();

    if (CONFIG.isPlaying) {
      AudioEngine.setVolume(0);
      if (playBtn) {
        playBtn.classList.remove('playing');
      }
      CONFIG.isPlaying = false;
    }

    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Focus Timer', {
        body: 'Time is up! Take a break.',
        icon: '⏱️',
      });
    }
  },

  playAlarm() {
    if (!AudioEngine.ctx) {
      AudioEngine.init();
    }

    const ctx = AudioEngine.ctx;
    if (!ctx) return;

    const now = ctx.currentTime;

    for (let i = 0; i < 3; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.value = 880;

      gain.gain.setValueAtTime(0, now + i * 0.3);
      gain.gain.linearRampToValueAtTime(0.3, now + i * 0.3 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.3 + 0.25);

      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.3);
      osc.stop(now + i * 0.3 + 0.3);
    }
  },

  start() {
    if (!CONFIG.timer.isRunning) {
      CONFIG.timer.isRunning = true;

      if (timerStartBtn) {
        timerStartBtn.textContent = 'Pause';
        timerStartBtn.classList.add('running');
      }

      CONFIG.timer.intervalId = setInterval(() => {
        CONFIG.timer.remaining--;
        this.updateDisplay();

        if (CONFIG.timer.remaining <= 0) {
          this.onTimerEnd();
        }
      }, 1000);
    } else {
      this.pause();
    }
  },

  pause() {
    CONFIG.timer.isRunning = false;
    if (CONFIG.timer.intervalId) {
      clearInterval(CONFIG.timer.intervalId);
    }

    if (timerStartBtn) {
      timerStartBtn.textContent = 'Start';
      timerStartBtn.classList.remove('running');
    }
  },

  reset() {
    CONFIG.timer.isRunning = false;
    if (CONFIG.timer.intervalId) {
      clearInterval(CONFIG.timer.intervalId);
    }
    CONFIG.timer.remaining = CONFIG.timer.duration;

    if (timerStartBtn) {
      timerStartBtn.textContent = 'Start';
      timerStartBtn.classList.remove('running');
    }

    this.updateDisplay();
  },

  setDuration(minutes: number) {
    CONFIG.timer.duration = minutes * 60;
    CONFIG.timer.remaining = minutes * 60;

    if (CONFIG.timer.isRunning) {
      this.pause();
    }

    this.updateDisplay();
  },
};
